
// File: AddContent.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  message,
  Card,
  Switch,
  Upload,
  Typography,
} from "antd";
import {
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { supabase } from "../../config/supabaseClient";
import { useNavigate } from "react-router-dom";
import JoditEditor from "jodit-react";

import Papa from "papaparse";
import mammoth from "mammoth";

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;

const AddContent = () => {
  const navigate = useNavigate();
  const editor = useRef(null);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [addingClass, setAddingClass] = useState(false);
  const [newClass, setNewClass] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [isMCQ, setIsMCQ] = useState(false);
  const [mcqs, setMcqs] = useState([
    { question: "", options: ["", "", "", ""], correctAnswer: "", logic: "" },
  ]);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [noteFile, setNoteFile] = useState(null);
  const [noteUploading, setNoteUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingSubCategory, setAddingSubCategory] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch classes
        const { data: fetchedClasses, error: classError } = await supabase.from("classes").select("*");
        if (classError) throw classError;
        setClasses(fetchedClasses);

        // Fetch subcategories
        const { data: fetchedSubCategories, error: subCatError } = await supabase.from("subcategories").select("*");
        if (subCatError) throw subCatError;
        setSubCategories(fetchedSubCategories);

        // Load draft from localStorage
        const draft = JSON.parse(localStorage.getItem("draft"));
        if (draft) {
          setDescription(draft.description || "");
          form.setFieldsValue(draft);
          if (draft.mcqs) {
            setMcqs(draft.mcqs);
            setIsMCQ(true);
          }
          if (draft.featuredImage) {
            setFeaturedImage(draft.featuredImage);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to load initial data.");
      }
    };

    fetchData();
  }, [form]);

  /***********************
   * File upload utilities
   ***********************/
  const uploadFileToSupabase = async (file, bucket, onProgress) => {
    try {
      const uniqueFileName = `${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(uniqueFileName, file, {
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              onProgress(percent);
            }
          }
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(uniqueFileName);
        
      return publicUrlData.publicUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const uploadFeaturedImage = async (file) => {
    setImageUploading(true);
    try {
      const downloadURL = await uploadFileToSupabase(file, "featured-images");
      setFeaturedImage(downloadURL);
      message.success("Featured image uploaded successfully!", 3);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading featured image:", error);
      message.error("Error uploading featured image", 3);
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  const uploadNoteFile = async (file) => {
    setNoteUploading(true);
    try {
      const downloadURL = await uploadFileToSupabase(file, "notes-files");
      setNoteFile(downloadURL);
      message.success("Note file uploaded!");
      return downloadURL;
    } catch (error) {
      console.error("Upload error:", error);
      message.error("PDF upload failed");
      throw error;
    } finally {
      setNoteUploading(false);
    }
  };

  /***********************
   * MCQ bulk import logic
   ***********************/

  const normalizeMCQHeader = (header) =>
    String(header ?? "")
      .replace(/^\uFEFF/, "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

  const getMCQField = (row, keys) => {
    for (const key of keys) {
      const value = row[normalizeMCQHeader(key)];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
    return "";
  };

  const normalizeParsedMCQ = (item) => {
    const options = (item.options ?? ["", "", "", ""])
      .slice(0, 4)
      .map((option) => (option == null ? "" : String(option).trim()));

    let correctAnswer = (item.correctAnswer ?? item.correct ?? item.answer ?? "")
      .toString()
      .trim();

    if (/^[A-D]$/i.test(correctAnswer)) {
      const idx = { A: 0, B: 1, C: 2, D: 3 }[correctAnswer.toUpperCase()];
      correctAnswer = options[idx] || "";
    }

    return {
      question: (item.question ?? "").toString().trim(),
      options,
      correctAnswer,
      logic: (item.logic ?? "").toString().trim(),
      section: (item.section ?? "").toString().trim(),
      number: Number(item.number ?? 0) || 0,
    };
  };

  const isEmptyMCQ = (mcq) =>
    !mcq.question &&
    mcq.options.every((option) => !option) &&
    !mcq.correctAnswer &&
    !mcq.logic &&
    !mcq.section &&
    !mcq.number;

  const splitTextIntoMCQBlocks = (rawText) => {
    const cleaned = rawText.replace(/\r/g, "").trim();
    if (!cleaned) return [];

    const numberedBlocks = cleaned
      .split(/\n(?=\s*(?:\d+[\.\)]|Q(?:uestion)?\s*\d+[:\.\)]))/i)
      .map((block) => block.trim())
      .filter(Boolean);

    if (numberedBlocks.length > 1) return numberedBlocks;

    const lines = cleaned
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const blocks = [];
    let current = null;
    const optionLineRegex = /^[A-Da-d][\.\)]\s*(.+)$/;

    lines.forEach((line) => {
      const optionMatch = line.match(optionLineRegex);

      if (!current) {
        current = { questionLines: [line], optionLines: [] };
        return;
      }

      if (optionMatch) {
        current.optionLines.push(optionMatch[1].trim());
        if (current.optionLines.length === 4) {
          blocks.push(
            [current.questionLines.join(" ").trim(), ...current.optionLines].join("\n")
          );
          current = null;
        }
        return;
      }

      if (current.optionLines.length === 0) {
        current.questionLines.push(line);
        return;
      }

      blocks.push(
        [current.questionLines.join(" ").trim(), ...current.optionLines].join("\n")
      );
      current = { questionLines: [line], optionLines: [] };
    });

    if (current) {
      blocks.push(
        [current.questionLines.join(" ").trim(), ...current.optionLines].join("\n")
      );
    }

    return blocks.filter(Boolean);
  };

  // Robust CSV parser that accepts headers optionA..optionD or option1..option4 (and variants)
  const parseCSVFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const csvText = String(reader.result ?? "")
            .replace(/^\uFEFF/, "")
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n");

          const results = Papa.parse(csvText, {
            header: true,
            skipEmptyLines: "greedy",
            transformHeader: normalizeMCQHeader,
          });

          if (results.errors?.length) {
            console.warn("CSV parse warnings:", results.errors);
          }

          const rows = Array.isArray(results.data) ? results.data : [];
          const parsed = rows
            .map((row) => {
              const options = [
                getMCQField(row, ["optionA", "option1", "opt1", "a"]),
                getMCQField(row, ["optionB", "option2", "opt2", "b"]),
                getMCQField(row, ["optionC", "option3", "opt3", "c"]),
                getMCQField(row, ["optionD", "option4", "opt4", "d"]),
              ];

              return normalizeParsedMCQ({
                section: getMCQField(row, ["section"]),
                number: getMCQField(row, ["number", "no", "srno", "serialno"]),
                question: getMCQField(row, ["question", "ques", "q"]),
                options,
                correctAnswer: getMCQField(row, ["correct", "correctanswer", "answer"]),
                logic: getMCQField(row, ["logic", "explanation", "reason"]),
              });
            })
            .filter((mcq) => !isEmptyMCQ(mcq));

          resolve(parsed);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () => reject(reader.error || new Error("Failed to read CSV file"));
      reader.readAsText(file);
    });

  // Heuristic DOCX/TXT parser (best-effort)
  const parseDocxOrTxtFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    let rawText = "";
    if (file.name.toLowerCase().endsWith(".docx")) {
      const result = await mammoth.extractRawText({ arrayBuffer });
      rawText = result.value || "";
    } else {
      rawText = new TextDecoder("utf-8").decode(arrayBuffer);
    }

    const questionBlocks = splitTextIntoMCQBlocks(rawText);

    const parsed = [];

    questionBlocks.forEach((block) => {
      // extract question text
      const qTextMatch = block.match(/^(\d+[\.\)]\s*)?(.*?)((?=\n[A-Da-d][\.\)]\s)|(?=\nA\))|(?=\nA\.)|$)/s);
      const questionText = qTextMatch ? qTextMatch[2].trim() : block.split("\n")[0].trim();

      // find A-D options by scanning lines
      const optionRegex = /^[A-Da-d][\.\)]\s*(.+)$/m;
      const optionMatches = [];
      const linesOfBlock = block.split("\n");
      for (let i = 0; i < linesOfBlock.length; i++) {
        const line = linesOfBlock[i].trim();
        const m = line.match(optionRegex);
        if (m) optionMatches.push(m[1].trim());
      }

      // fallback: inline matches
      if (optionMatches.length < 4) {
        const inlineMatches = [...block.matchAll(/[A-Da-d][\.\)]\s*([^A-Da-d\.\)]{1,200}?)(?=(?:[A-Da-d][\.\)]|$))/g)];
        if (inlineMatches && inlineMatches.length) {
          optionMatches.length = 0;
          inlineMatches.forEach((im) => optionMatches.push((im[1] || "").trim()));
        }
      }

      // fallback: use lines after first blank block as options
      if (optionMatches.length < 4) {
        const afterFirstBlank = block.split(/\n\n+/).slice(1).join("\n").split("\n").map(l => l.trim()).filter(Boolean);
        if (afterFirstBlank.length) optionMatches.push(...afterFirstBlank.slice(0, 4));
      }

      while (optionMatches.length < 4) optionMatches.push("");

      parsed.push(
        normalizeParsedMCQ({
          section: "",
          number: 0,
          question: questionText,
          options: optionMatches.slice(0, 4),
          correctAnswer: "",
          logic: "",
        })
      );
    });

    return parsed.filter((mcq) => !isEmptyMCQ(mcq));
  };

  // General handler for MCQ file upload
  const handleMCQFile = async (file) => {
    const name = file.name.toLowerCase();
    message.loading("Parsing MCQ file...", 0);
    try {
      let parsed = [];
      if (name.endsWith(".csv")) {
        parsed = await parseCSVFile(file);
      } else if (name.endsWith(".txt") || name.endsWith(".docx")) {
        parsed = await parseDocxOrTxtFile(file);
      } else {
        message.destroy();
        message.error("Unsupported file type. Use CSV, DOCX or TXT.");
        return;
      }

      const normalized = parsed
        .map((p) => normalizeParsedMCQ(p))
        .filter((mcq) => !isEmptyMCQ(mcq));

      if (!normalized.length) {
        message.destroy();
        message.error("No valid MCQs were found in the uploaded file.");
        return;
      }

      normalized.sort((a, b) => {
        if ((a.section || "") === (b.section || "")) return (a.number || 0) - (b.number || 0);
        return String(a.section || "").localeCompare(String(b.section || ""), undefined, { numeric: true });
      });

      setMcqs((prev) => {
        const hasBlankTemplate =
          prev.length === 1 && isEmptyMCQ(normalizeParsedMCQ(prev[0]));
        const base = hasBlankTemplate ? [] : prev.slice();
        return [
          ...base,
          ...normalized.map(({ question, options, correctAnswer, logic, section, number }) => ({
            question,
            options,
            correctAnswer,
            logic,
            section,
            number,
          })),
        ];
      });

      setIsMCQ(true);
      message.destroy();
      message.success(`Imported ${normalized.length} MCQ(s) successfully`);
    } catch (err) {
      console.error("MCQ import error:", err);
      message.destroy();
      message.error("Failed to parse MCQ file (see console)");
    }
  };

  /***********************
   * Existing handlers left unchanged (with slight wiring to new MCQ upload)
   ***********************/
  const onFinish = async (values) => {
    const { topic, class: selectedClasses, category, subCategory } = values;
    setUploading(true);

    let featuredImageUrl = null;
    if (featuredImage) {
      featuredImageUrl = featuredImage;
    }

    try {
      const topicData = {
        topic: topic || "",
        class: selectedClasses.join(", "),
        category: category || "",
        subCategory: isMCQ ? "MCQ Test" : subCategory,
        description: description || "",
        mcqs: isMCQ ? mcqs : [],
        timestamp: new Date().toISOString(),
        featuredImage: featuredImageUrl,
        notesFile: noteFile,
      };

      const { error } = await supabase.from("topics").insert([topicData]);
      if (error) throw error;
      message.success("Topic created successfully!", 3);
      localStorage.removeItem("draft");

      // Reset form and state to allow submitting another topic
      form.resetFields();
      setDescription("");
      setFeaturedImage(null);
      setNoteFile(null);
      setMcqs([{ question: "", options: ["", "", "", ""], correctAnswer: "", logic: "" }]);
      setIsMCQ(false);
    } catch (e) {
      console.error("Error adding document:", e);
      message.error("Failed to save topic.", 3);
    } finally {
      setUploading(false);
    }
  };

  const handleAddClass = async () => {
    if (newClass && !classes.some((cls) => cls.name === newClass)) {
      setAddingClass(true);
      try {
        const { data, error } = await supabase.from("classes").insert([{ name: newClass }]).select();
        if (error) throw error;
        setClasses([...classes, { id: data[0].id, name: newClass }]);
        setNewClass("");
        message.success(`Class ${newClass} added successfully!`, 3);
      } catch (e) {
        console.error("Error adding class:", e);
        message.error("Failed to add class.", 3);
      } finally {
        setAddingClass(false);
      }
    }
  };

  const handleAddCategory = async () => {
    if (newCategory && !categories.some((cat) => cat.name === newCategory)) {
      setAddingCategory(true);
      try {
        const { data, error } = await supabase.from("categories").insert([{ name: newCategory }]).select();
        if (error) throw error;
        setCategories([...categories, { id: data[0].id, name: newCategory }]);
        setNewCategory("");
        message.success(`Category ${newCategory} added successfully!`, 3);
      } catch (e) {
        console.error("Error adding category:", e);
        message.error("Failed to add category.", 3);
      } finally {
        setAddingCategory(false);
      }
    }
  };

  const handleAddSubCategory = async () => {
    if (
      newSubCategory &&
      !subCategories.some((sub) => sub.name === newSubCategory)
    ) {
      setAddingSubCategory(true);
      try {
        const { data, error } = await supabase.from("subcategories").insert([{ name: newSubCategory }]).select();
        if (error) throw error;
        setSubCategories([
          ...subCategories,
          { id: data[0].id, name: newSubCategory },
        ]);
        setNewSubCategory("");
        message.success(`Subcategory ${newSubCategory} added successfully!`, 3);
      } catch (e) {
        console.error("Error adding subcategory:", e);
        message.error("Failed to add subcategory.", 3);
      } finally {
        setAddingSubCategory(false);
      }
    }
  };

  const handleSaveDraft = async (values) => {
    setSavingDraft(true);
    try {
      const draftData = {
        ...values,
        description,
        mcqs,
        featuredImage,
      };
      localStorage.setItem("draft", JSON.stringify(draftData));
      message.success("Draft saved successfully!", 3);
    } catch (error) {
      message.error("Error saving draft", 3);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleClearDraft = () => {
    localStorage.removeItem("draft");
    form.resetFields();
    setDescription("");
    setMcqs([
      { question: "", options: ["", "", "", ""], correctAnswer: "", logic: "" },
    ]);
    setIsMCQ(false);
    setFeaturedImage(null);
    message.success("Draft cleared!", 3);
  };

  const handleMCQChange = (index, key, value) => {
    const updatedMcqs = [...mcqs];
    updatedMcqs[index][key] = value;
    setMcqs(updatedMcqs);
  };

  const handleOptionChange = (mcqIndex, optionIndex, value) => {
    const updatedMcqs = [...mcqs];
    const oldOption = updatedMcqs[mcqIndex].options[optionIndex];
    updatedMcqs[mcqIndex].options[optionIndex] = value;

    // Update correct answer if it was the changed option
    if (updatedMcqs[mcqIndex].correctAnswer === oldOption) {
      updatedMcqs[mcqIndex].correctAnswer = value;
    }

    setMcqs(updatedMcqs);
  };

  const handleCorrectAnswerChange = (mcqIndex, value) => {
    const updatedMcqs = [...mcqs];
    updatedMcqs[mcqIndex].correctAnswer = value;
    setMcqs(updatedMcqs);
  };

  const handleAddMCQ = () => {
    setMcqs([
      ...mcqs,
      { question: "", options: ["", "", "", ""], correctAnswer: "", logic: "" },
    ]);
  };

  const handleRemoveMCQ = (index) => {
    if (mcqs.length > 1) {
      const updatedMcqs = [...mcqs];
      updatedMcqs.splice(index, 1);
      setMcqs(updatedMcqs);
    } else {
      message.warning("At least one MCQ is required");
    }
  };

  const renderMCQTemplate = () => {
    return mcqs.map((mcq, index) => (
      <div
        key={index}
        style={{
          marginBottom: "16px",
          padding: "16px",
          border: "1px solid #f0f0f0",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h4>MCQ {index + 1}</h4>
          <Button
            type="link"
            danger
            onClick={() => handleRemoveMCQ(index)}
            disabled={mcqs.length <= 1}
          >
            Remove
          </Button>
        </div>
        <Form.Item label="Question" required>
          <JoditEditor
            value={mcq.question}
            config={mcqJoditConfig}
            onChange={(newContent) =>
              handleMCQChange(index, "question", newContent)
            }
          />
        </Form.Item>
        <Form.Item label="Options" required>
          {mcq.options.map((option, optionIndex) => (
            <div key={optionIndex} style={{ marginBottom: "8px" }}>
              <Input
                addonBefore={
                  <input
                    type="radio"
                    name={`correct-${index}`}
                    checked={mcq.correctAnswer === option}
                    onChange={() => handleCorrectAnswerChange(index, option)}
                  />
                }
                value={option}
                onChange={(e) =>
                  handleOptionChange(index, optionIndex, e.target.value)
                }
                placeholder={`Option ${optionIndex + 1}`}
              />
            </div>
          ))}
        </Form.Item>
        <Form.Item label="Logic for Correct Answer (Optional)">
          <JoditEditor
            value={mcq.logic}
            config={mcqJoditConfig}
            onChange={(newContent) =>
              handleMCQChange(index, "logic", newContent)
            }
          />
        </Form.Item>
      </div>
    ));
  };

  // Custom image upload handler for Jodit
  const handleImageUpload = (editor, file) => {
    const key = 'jodit-image-upload';
    return new Promise((resolve, reject) => {
      message.loading({ content: 'Uploading image... 0%', key, duration: 0 });

      uploadFileToSupabase(file, 'featured-images', (percent) => {
        message.loading({ content: `Uploading image... ${percent}%`, key, duration: 0 });
      })
      .then(downloadURL => {
        message.success({ content: 'Image uploaded successfully!', key, duration: 2 });
        resolve(downloadURL);
      })
      .catch(error => {
        uploadFileToSupabase(file, 'editor-images', (percent) => {
          message.loading({ content: `Uploading image... ${percent}%`, key, duration: 0 });
        })
        .then(downloadURL => {
          message.success({ content: 'Image uploaded successfully!', key, duration: 2 });
          resolve(downloadURL);
        })
        .catch(fallbackError => {
          message.error({ content: 'Image upload failed', key, duration: 2 });
          console.error('Image upload error:', fallbackError);
          reject(fallbackError);
        });
      });
    });
  };

  // Custom file upload handler for Jodit
  const handleFileUpload = (editor, file) => {
    const key = 'jodit-file-upload';
    return new Promise((resolve, reject) => {
      message.loading({ content: 'Uploading file... 0%', key, duration: 0 });

      uploadFileToSupabase(file, 'notes-files', (percent) => {
        message.loading({ content: `Uploading file... ${percent}%`, key, duration: 0 });
      })
      .then(downloadURL => {
        message.success({ content: 'File uploaded successfully!', key, duration: 2 });
        resolve(downloadURL);
      })
      .catch(error => {
        uploadFileToSupabase(file, 'editor-files', (percent) => {
          message.loading({ content: `Uploading file... ${percent}%`, key, duration: 0 });
        })
        .then(downloadURL => {
          message.success({ content: 'File uploaded successfully!', key, duration: 2 });
          resolve(downloadURL);
        })
        .catch(fallbackError => {
          uploadFileToSupabase(file, 'editor-images', (percent) => {
            message.loading({ content: `Uploading file... ${percent}%`, key, duration: 0 });
          })
          .then(downloadURL => {
            message.success({ content: 'File uploaded successfully!', key, duration: 2 });
            resolve(downloadURL);
          })
          .catch(err => {
            message.error({ content: 'File upload failed', key, duration: 2 });
            console.error('File upload error:', err);
            reject(err);
          });
        });
      });
    });
  };

  // Jodit config - unchanged
  const joditConfig = {
    readonly: false,
    height: 300,
    width: "100%",
    buttons: [
      "source",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "ul",
      "ol",
      "|",
      "font",
      "fontsize",
      "brush",
      "paragraph",
      "|",
      "align",
      "outdent",
      "indent",
      "|",
      "cut",
      "copy",
      "paste",
      "copyformat",
      "|",
      "hr",
      "table",
      "link",
      "|",
      "undo",
      "redo",
      "|",
      "preview",
      "print",
      "find",
      "fullsize",
      "image",
      "uploadImage",
      "video",
      "file",
      "uploadFile",
    ],
    uploader: {
      insertImageAsBase64URI: false,
      url: false,
      format: "json",
      imagesExtensions: ["jpg", "png", "jpeg", "gif"],
      filesVariableName: "files",
      withCredentials: false,
      isSuccess: (resp) => true,
      getMessage: (resp) => "",
      process: (resp) => ({
        files: [resp] || [],
        path: "",
        baseurl: "",
        error: 0,
        message: "",
      }),
      defaultHandlerSuccess: (data) => {
        if (data && data.files && data.files.length) {
          return data.files[0];
        }
        return "";
      },
      prepareData: function (formData) {
        return formData;
      },
      error: (e) => {
        console.error("Upload error:", e);
        message.error("File upload failed");
      },
    },
    imageDefaultWidth: 300,
    imagePosition: "center",
    spellcheck: true,
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: true,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: true,
    allowTabNavigtion: false,
    placeholder: "Type your content here...",
    image: {
      dialog: {
        tabs: {
          'Upload': {
            fields: {
              file: {
                name: 'file',
                label: 'Upload image from computer',
                type: 'file',
                accept: 'image/*',
                required: true
              }
            }
          },
          'URL': {
            fields: {
              url: {
                name: 'url',
                label: 'URL',
                type: 'text',
                required: true
              }
            }
          }
        }
      },
      upload: async (file) => {
        try {
          const url = await handleImageUpload(null, file);
          return { files: [url], isImages: [true] };
        } catch (error) {
          throw new Error('Upload failed');
        }
      },
      insert: function (url, alt, styles) {
        const image = this.selection.j.createInside.element('img');
        image.setAttribute('src', url);
        image.setAttribute('alt', url);
        this.s.insertNode(image);
      }
    },
    file: {
      dialog: {
        tabs: {
          'Upload': {
            fields: {
              file: {
                name: 'file',
                label: 'Upload file from computer',
                type: 'file',
                required: true
              }
            }
          },
          'URL': {
            fields: {
              url: {
                name: 'url',
                label: 'URL',
                type: 'text',
                required: true
              }
            }
          }
        }
      },
      upload: async (file) => {
        try {
          const url = await handleFileUpload(null, file);
          return { files: [url] };
        } catch (error) {
          throw new Error('Upload failed');
        }
      },
      insert: function (url, title) {
        const link = this.selection.j.createInside.element('a');
        link.setAttribute('href', url);
        link.setAttribute('target', '_blank');
        link.innerText = title || 'Download File';
        this.s.insertNode(link);
      }
    },
    controls: {
      uploadImage: {
        icon: 'image',
        exec: function (editor) {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = async () => {
            if (input.files && input.files[0]) {
              try {
                const file = input.files[0];
                const url = await handleImageUpload(null, file);
                
                const image = document.createElement('img');
                image.src = url;
                image.alt = url;
                editor.selection.insertNode(image);
              } catch (err) {
                console.error("Upload failed", err);
              }
            }
          };
          input.click();
        },
        tooltip: 'Upload Image from PC'
      },
      uploadFile: {
        icon: 'file',
        exec: function (editor) {
          const input = document.createElement('input');
          input.type = 'file';
          input.onchange = async () => {
            if (input.files && input.files[0]) {
              try {
                const file = input.files[0];
                const url = await handleFileUpload(null, file);
                
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.innerText = file.name || 'Download File';
                editor.selection.insertNode(link);
              } catch (err) {
                console.error("Upload failed", err);
              }
            }
          };
          input.click();
        },
        tooltip: 'Upload File from PC'
      }
    }
  };

  const mcqJoditConfig = {
    ...joditConfig,
    height: 150,
    buttons:
      "bold,italic,underline,strikethrough,ul,ol,font,fontsize,brush,paragraph,align,link,image",
  };

  const beforeImageUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
    }
    return isImage && isLt5M;
  };

  const handleFeaturedImageUpload = async (options) => {
    const { file } = options;
    try {
      const url = await uploadFeaturedImage(file);
      return url;
    } catch (error) {
      console.error("Image upload error:", error);
      return null;
    }
  };

  return (
    <div className="form-container mt-2">
      <h1 className="text-center mb-2">Create Topic</h1>
      <Card
        bordered={false}
        style={{ margin: "20px auto", width: "100%", borderRadius: "10px" }}
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          form={form}
        >
          {/* --- Featured Image (unchanged) --- */}
          <Form.Item label="Featured Image (Optional)">
            <Dragger
              name="featuredImage"
              multiple={false}
              beforeUpload={beforeImageUpload}
              customRequest={handleFeaturedImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag image to upload</p>
              <p className="ant-upload-hint">
                Upload a featured image for this topic (Max 5MB)
              </p>
            </Dragger>
            {featuredImage && (
              <div style={{ marginTop: "16px" }}>
                <img
                  src={featuredImage}
                  alt="Featured preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "200px",
                    borderRadius: "4px",
                  }}
                />
                <Button
                  type="link"
                  danger
                  onClick={() => setFeaturedImage(null)}
                  style={{ marginTop: "8px" }}
                >
                  Remove Image
                </Button>
              </div>
            )}
            {imageUploading && (
              <LoadingOutlined style={{ marginLeft: "8px" }} />
            )}
          </Form.Item>

          {/* --- Topic Name + Class + Category + SubCategory (unchanged) --- */}
          <Form.Item
            label="Topic Name"
            name="topic"
            rules={[{ required: true, message: "Please enter topic name!" }]}
          >
            <Input placeholder="Enter topic name" />
          </Form.Item>

          <Form.Item
            label="Class"
            name="class"
            rules={[{ required: true, message: "Please select a class!" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select class(es)"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ display: "flex", padding: 8 }}>
                    <Input
                      style={{ flex: "auto" }}
                      placeholder="Add new class"
                      value={newClass}
                      onChange={(e) => setNewClass(e.target.value)}
                      onPressEnter={handleAddClass}
                    />
                    <Button
                      type="primary"
                      icon={
                        addingClass ? <LoadingOutlined /> : <PlusOutlined />
                      }
                      onClick={handleAddClass}
                    >
                      {addingClass ? "Adding..." : "Add"}
                    </Button>
                  </div>
                </>
              )}
            >
              {classes.map((cls) => (
                <Option key={cls.id} value={cls.name}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: false, message: "Please select a category!" }]}
          >
            <Select
              placeholder="Select category"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ display: "flex", padding: 8 }}>
                    <Input
                      style={{ flex: "auto" }}
                      placeholder="Add new category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onPressEnter={handleAddCategory}
                    />
                    <Button
                      type="primary"
                      icon={addingCategory ? <LoadingOutlined /> : <PlusOutlined />}
                      onClick={handleAddCategory}
                    >
                      {addingCategory ? "Adding..." : "Add"}
                    </Button>
                  </div>
                </>
              )}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.name}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="SubCategory"
            name="subCategory"
            rules={[
              { required: false, message: "Please select a subcategory!" },
            ]}
          >
            <Select
              placeholder="Select subcategory"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ display: "flex", padding: 8 }}>
                    <Input
                      style={{ flex: "auto" }}
                      placeholder="Add new subcategory"
                      value={newSubCategory}
                      onChange={(e) => setNewSubCategory(e.target.value)}
                      onPressEnter={handleAddSubCategory}
                    />
                    <Button
                      type="primary"
                      icon={
                        addingSubCategory ? <LoadingOutlined /> : <PlusOutlined />
                      }
                      onClick={handleAddSubCategory}
                    >
                      {addingSubCategory ? "Adding..." : "Add"}
                    </Button>
                  </div>
                </>
              )}
            >
              {subCategories.map((sub) => (
                <Option key={sub.id} value={sub.name}>
                  {sub.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* --- Notes Upload (unchanged) --- */}
          <Form.Item label="Attach Notes">
            <Upload
              accept=".pdf"
              showUploadList={false}
              beforeUpload={(file) => {
                const isValid = file.type === "application/pdf";
                const isLt10M = file.size / 1024 / 1024 < 10;

                if (!isValid) message.error("Only PDF files allowed!");
                if (!isLt10M) message.error("File must be smaller than 10MB!");

                return (isValid && isLt10M) || Upload.LIST_IGNORE;
              }}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const url = await uploadNoteFile(file);
                  onSuccess(url);
                } catch (err) {
                  onError(err);
                  message.error("File upload failed");
                }
              }}
            >
              <Button icon={<UploadOutlined />} loading={noteUploading}>
                {noteUploading ? "Uploading..." : "Upload PDF Notes"}
              </Button>
            </Upload>
            {noteFile && (
              <div style={{ marginTop: 8 }}>
                <a
                  href={noteFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginRight: 8 }}
                >
                  View Uploaded PDF
                </a>
                <Button type="link" danger onClick={() => setNoteFile(null)}>
                  Remove
                </Button>
              </div>
            )}
          </Form.Item>

          {/* --- MCQ Switch --- */}
          <Form.Item label="MCQ Test" name="mcqSwitch" valuePropName="checked">
            <Switch checked={isMCQ} onChange={(checked) => setIsMCQ(checked)} />
          </Form.Item>

          {/* --- Bulk MCQ Upload control (NEW) --- */}
          <Form.Item label="Bulk MCQ Upload (CSV, DOCX, TXT)">
            <Dragger
              name="mcqBulk"
              multiple={false}
              accept=".csv,.docx,.txt"
              showUploadList={false}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  await handleMCQFile(file);
                  onSuccess("ok");
                } catch (err) {
                  console.error(err);
                  onError(err);
                }
              }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to upload</p>
              <p className="ant-upload-hint">
                Upload CSV (recommended) or DOCX/TXT containing MCQs. CSV header should include: section,number,question,optionA,optionB,optionC,optionD,correct,logic
              </p>
            </Dragger>
            <Text type="secondary">
              Tip: CSV is the most reliable. DOCX parser is heuristic — check parsed MCQs below and edit if needed.
            </Text>
          </Form.Item>

          {/* --- Description (unchanged) --- */}
          <Form.Item label="Description">
            <JoditEditor
              ref={editor}
              value={description}
              config={joditConfig}
              onBlur={(newContent) => setDescription(newContent)}
            />
          </Form.Item>

          {/* --- Render MCQs (unchanged) --- */}
          {isMCQ && (
            <>
              {renderMCQTemplate()}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={handleAddMCQ}
                  block
                  icon={<PlusOutlined />}
                >
                  Add More MCQs
                </Button>
              </Form.Item>
            </>
          )}

          {/* --- Buttons (unchanged) --- */}
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={uploading}>
              {uploading ? "Submitting..." : "Submit"}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              type="default"
              block
              onClick={() => navigate("/managecontent")}
            >
              Manage Topics
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              type="default"
              block
              icon={<SaveOutlined />}
              onClick={() => handleSaveDraft(form.getFieldsValue())}
              loading={savingDraft}
            >
              {savingDraft ? "Saving Draft..." : "Save as Draft"}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button type="danger" block onClick={handleClearDraft}>
              Clear Draft
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AddContent;















